import React from "react";
import { Box, Container, Typography, Button, Stack } from "@mui/material";
import HeroBgImg from "../../assets/wellness-hero-bg.png";
import Avatar1 from "../../assets/avatar1.png";
import Avatar2 from "../../assets/avatar2.png";
import Avatar3 from "../../assets/avatar3.png";
// import useResponsive from "../../hooks/useResponsive";
import { useLandingPage } from "../../context/LandingPageContext";

export default function Hero() {
  // const { isMobile } = useResponsive();

  const { landingData } = useLandingPage();

  const totalUsers = landingData?.totalUsersRegistered || "50";

  const avatars =
    landingData?.stories?.length >= 3 ? landingData.stories.slice(0, 3) : [];

    const formatUsers = (count) => {
    if (!count) return "0";

    if (count < 1000) {
      return count.toString();
    }

    return `${Math.round(count / 1000)}k+`;
  };

  return (
    <Box
      id="home"
      sx={{
        position: "relative",
        height: "100vh",
        minHeight: "650px",
        maxHeight: "988px",
        width: "100%",
        display: "flex",
        alignItems: { xs: "flex-start", sm: "flex-start", md: "center" },
        overflow: "hidden",
        pt: { xs: "160px", sm: "360px", md: "100px" },
      }}
    >
      {/* Horizontally Flipped Background Image Layer */}
      <Box
        sx={{
          position: "absolute",
          top: "70px",
          left: "50%",
          right: 0,
          bottom: 0,
          width: "100%",
          maxWidth: "none",
          maxHeight: "none",
          backgroundImage: `url(${HeroBgImg})`,
          backgroundSize: {
            xs: "auto 100%",
            sm: "auto 100%",
            md: "cover",
            lg: "cover",
          },
          backgroundPosition: {
            xs: "20% center",
            sm: "22% center",
            md: "26% center",
            lg: "center",
          },
          backgroundRepeat: "no-repeat",
          transform: "translateX(-50%) scaleX(-1)", // Centered and flipped globally
          zIndex: 1,
        }}
      />

      {/* Background Decorative Blur Circle */}
      <Box
        sx={{
          position: "absolute",
          width: "50vw",
          height: "50vw",
          maxWidth: "600px",
          maxHeight: "600px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(176, 137, 104, 0.12) 0%, rgba(44, 94, 67, 0) 70%)",
          top: "-10%",
          right: "-5%",
          animation: "pulseSlow 8s ease-in-out infinite",
          pointerEvents: "none",
          zIndex: 2,
        }}
      />

      <Container
        maxWidth="xl"
        sx={{
          zIndex: 3,
          position: "relative",
          mt: { xs: 0, sm: 0, md: 0 },
          pb: { xs: "120px", sm: "180px", md: "200px" }, // Push content up to avoid bottom info cards overlap!
          pl: { xs: "20px", sm: "40px", md: "100px" },
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: "250px", sm: "550px", md: "800px" },
            color: "#FFFFFF",
          }}
        >
          {/* Main Title Heading */}
          <Typography
            variant="h1"
            className="animate-fade-in-up delay-100"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              color: "#325D44",
              fontWeight: 800,
              fontStyle: "normal" /* ExtraBold */,
              fontSize: { xs: "30px", sm: "40px", md: "48px" },
              leadingTrim: "cap-height",
              lineHeight: "140%",
              letterSpacing: "0%",
              textAlign: "left",
              verticalAlign: "middle",
              // mb: { xs: 1.5, md: 2.5 },
              pt: { xs: 0, sm: 0, md: "180px" },
              // textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              "& span": {
                fontStyle: "italic",
              },
            }}
          >
            Find Your Calm.
          </Typography>

          <Typography
            variant="h1"
            className="animate-fade-in-up delay-100"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              color: "#639477ff",
              fontWeight: 800,
              fontStyle: "normal" /* ExtraBold */,
              fontSize: { xs: "30px", sm: "40px", md: "48px" },
              leadingTrim: "cap-height",
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "left",
              verticalAlign: "middle",
              mb: { xs: 1.5, md: 2.5 },

              // textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              "& span": {
                fontStyle: "italic",
              },
            }}
          >
            Breathe Better.
          </Typography>

          {/* Subtext Paragraph */}
          <Typography
            variant="body1"
            className="animate-fade-in-up delay-200"
            sx={{
              fontSize: {
                xs: "0.95rem",
                sm: "1.05rem",
                md: "1.15rem",
                lg: "22px",
              },
              fontWeight: 400,
              fontFamily: "poppins,400",
              color: "#657069",
              mb: { xs: 3, sm: 4, md: 6 },
              opacity: 0.9,
              maxWidth: "650px",
              textShadow: "0 1px 4px rgba(0, 0, 0, 0.15)",
            }}
          >
            Guided meditation and breathing for a peaceful mind — built around
            the rhythms of your day.
          </Typography>

          {/* Call to Actions */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            className="animate-fade-in-up delay-300"
            sx={{ maxWidth: { xs: "100%", sm: "460px" } }}
          >
            <Button
              variant="contained"
              size="large"
              href="#features"
              sx={{
                backgroundColor: "#325D44",
                px: { xs: 3.5, sm: 4.5 },
                py: { xs: 1.2, sm: 1.5 },
                fontSize: { xs: "0.9rem", md: "0.95rem" },
                fontWeight: 600,
                width: { xs: "80%", sm: "220px" },
                borderRadius: "16px",
                "&:hover": {
                  backgroundColor: "#1B3B2B",
                  boxShadow: "0 4px 14px rgba(44, 94, 67, 0.25)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Explore Features
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="#download"
              sx={{
                px: { xs: 3.5, sm: 4.5 },
                py: { xs: 1.2, sm: 1.5 },
                fontSize: { xs: "0.9rem", md: "0.95rem" },
                fontWeight: 600,
                color: "#325D44",
                borderColor: "#325D44",
                width: { xs: "80%", sm: "220px" },
                backgroundColor: "#ffffffff",
                borderWidth: "1.5px",
                borderRadius: "16px",
                "&:hover": {
                  borderColor: "#1f3f2dff",
                  borderWidth: "1.5px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              Download now
            </Button>
          </Stack>

          {/* Social Proof Avatars & Text */}
          <Box
            className="animate-fade-in-up delay-300"
            sx={{
              display: "flex",
              alignItems: "center",
              mt: { xs: 4, sm: 8 },
              gap: 2.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="img"
                src={avatars[0]?.photoUrl || Avatar1}
                alt="User Avatar 1"
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: "50%",
                  border: "2px solid #FFFFFF",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  objectFit: "cover",
                  zIndex: 3,
                }}
              />
              <Box
                component="img"
                src={avatars[1]?.photoUrl || Avatar2}
                alt="User Avatar 2"
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: "50%",
                  border: "2px solid #FFFFFF",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  objectFit: "cover",
                  marginLeft: "-14px",
                  zIndex: 2,
                }}
              />
              <Box
                component="img"
                src={avatars[2]?.photoUrl || Avatar3}
                alt="User Avatar 3"
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: "50%",
                  border: "2px solid #FFFFFF",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  objectFit: "cover",
                  marginLeft: "-14px",
                  zIndex: 1,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: "'Poppins',400",
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: 400,
                color: "#657069",
                letterSpacing: "-0.01em",
              }}
            >
              {formatUsers(totalUsers)}+ peaceful minds
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
